import { useEffect, useMemo, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { BookOpen, Download, Printer, FileText } from "lucide-react";

export default function SalesPlaybook() {
  const [docs, setDocs] = useState(null);
  const [active, setActive] = useState(0);
  const contentRef = useRef(null);

  useEffect(() => {
    api.get("/playbook").then((r) => setDocs(r.data.docs)).catch(() => setDocs([]));
  }, []);

  const doc = useMemo(() => (docs && docs[active]) || null, [docs, active]);

  const downloadMd = () => {
    if (!doc) return;
    const blob = new Blob([doc.content], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = doc.filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const printPdf = () => {
    if (!contentRef.current) return;
    const w = window.open("", "_blank");
    if (!w) return;
    w.document.write(`<!doctype html><html><head><title>${doc?.title || "RetainProof"}</title>
      <style>
        body{font-family:Georgia,serif;max-width:780px;margin:40px auto;padding:0 24px;color:#111;line-height:1.6}
        h1,h2,h3{font-family:Arial,sans-serif;line-height:1.25}
        h1{font-size:26px} h2{font-size:20px;border-bottom:1px solid #ddd;padding-bottom:4px;margin-top:28px} h3{font-size:16px}
        table{border-collapse:collapse;width:100%;margin:16px 0;font-size:13px}
        th,td{border:1px solid #ccc;padding:6px 10px;text-align:left}
        th{background:#f3f3f3}
        code{background:#f3f3f3;padding:1px 4px;border-radius:3px}
        blockquote{border-left:3px solid #ccc;margin:12px 0;padding-left:14px;color:#444}
        hr{border:none;border-top:1px solid #ddd;margin:24px 0}
      </style></head><body>${contentRef.current.innerHTML}
      <script>window.onload=function(){window.print();}<\/script></body></html>`);
    w.document.close();
  };

  return (
    <div data-testid="sales-playbook-page">
      <div className="mb-8 flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <p className="font-mono-data text-xs uppercase tracking-[0.2em] text-[#B54747] flex items-center gap-2">
            <BookOpen className="h-3.5 w-3.5" /> Internal
          </p>
          <h1 className="font-display text-4xl font-light mt-1">Sales Playbook</h1>
          <p className="text-sm text-zinc-500 mt-2 max-w-2xl">
            Training manual, scripts, implementation guide and security briefs for the RetainProof sales team.
          </p>
        </div>
        {doc && (
          <div className="flex gap-2 shrink-0">
            <Button variant="outline" onClick={downloadMd} className="rounded-none" data-testid="playbook-download-md">
              <Download className="h-4 w-4 mr-2" /> Download .md
            </Button>
            <Button onClick={printPdf} className="rounded-none bg-[#0B1F33] text-white hover:bg-zinc-800" data-testid="playbook-print-pdf">
              <Printer className="h-4 w-4 mr-2" /> Print / PDF
            </Button>
          </div>
        )}
      </div>

      {!docs ? (
        <Skeleton className="h-96 w-full" />
      ) : docs.length === 0 ? (
        <p className="text-sm text-zinc-500" data-testid="playbook-empty">No playbook documents found.</p>
      ) : (
        <div className="grid lg:grid-cols-[260px_1fr] gap-6">
          <nav className="space-y-1" data-testid="playbook-nav">
            {docs.map((d, i) => (
              <button
                key={d.key}
                onClick={() => setActive(i)}
                data-testid={`playbook-tab-${d.key}`}
                className={`w-full text-left px-4 py-3 border text-sm flex items-center gap-2 transition-colors ${
                  i === active ? "border-[#0B1F33] bg-[#0B1F33] text-white" : "border-zinc-200 bg-white text-zinc-700 hover:border-zinc-400"
                }`}
              >
                <FileText className="h-4 w-4 shrink-0" /> {d.title}
              </button>
            ))}
          </nav>

          <article className="border border-zinc-200 bg-white p-8 min-w-0" data-testid="playbook-content">
            <div ref={contentRef} className="playbook-md">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{doc?.content || ""}</ReactMarkdown>
            </div>
          </article>
        </div>
      )}
    </div>
  );
}
